import { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import { toast } from "react-toastify";
import "./CartDrawer.css";

const getBikeImageUrl = (image) => {
  if (!image) return "/placeholder-bike.jpg";
  const clean = image.trim();
  if (clean.startsWith("http://") || clean.startsWith("https://")) return clean;
  return `https://bros-bike-rental-api.onrender.com/${clean.replace(/^\/+/, "")}`;
};

function CartDrawer() {
  const { cartItems, cartOpen, setCartOpen, removeFromCart, isRemoving } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Track loading state per cart item and batch proceeding
  const [loadingId, setLoadingId] = useState(null);
  const [proceedingAll, setProceedingAll] = useState(false);

  // Cart is only available to logged-in users
  if (!user || !cartOpen) return null;

  /* ── Proceed to Payment for ALL vehicles in cart ── */
  const handleProceedAll = async () => {
    if (cartItems.length === 0) return;
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to proceed with booking");
      setCartOpen(false);
      navigate("/login");
      return;
    }

    try {
      setProceedingAll(true);
      const res = await axiosClient.post("/bookings/batch", {
        items: cartItems.map((item) => ({
          bikeId: item.bikeId,
          startDate: item.startDate,
          endDate: item.endDate,
          totalAmount: item.totalAmount,
        })),
      });

      const bookingIds = res.data.bookingIds || [];
      if (bookingIds.length === 0) {
        throw new Error("No bookings were created");
      }

      setCartOpen(false);
      navigate(`/payment?booking=${bookingIds.join(",")}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Booking creation failed. Please try again.");
    } finally {
      setProceedingAll(false);
    }
  };

  /* ── Create booking for a single item then navigate to payment ── */
  const handleProceed = async (item) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login to proceed with booking");
      setCartOpen(false);
      navigate("/login");
      return;
    }

    try {
      setLoadingId(item.id);
      const res = await axiosClient.post("/bookings", {
        bikeId:      item.bikeId,
        startDate:   item.startDate,
        endDate:     item.endDate,
        totalAmount: item.totalAmount,
      });

      const bookingId = res.data._id || res.data.booking?._id;
      setCartOpen(false);
      navigate(`/payment?booking=${bookingId}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Booking failed. Please try again.");
    } finally {
      setLoadingId(null);
    }
  };


  const totalCartValue = cartItems.reduce((sum, i) => sum + i.totalAmount, 0);

  return (
    <>
      {/* Backdrop */}
      <div className="cart-backdrop" onClick={() => setCartOpen(false)} />

      {/* Drawer */}
      <div className="cart-drawer">
        <div className="cart-drawer-header">
          <h2 className="cart-drawer-title">
            🛒 Your Cart
            {cartItems.length > 0 && (
              <span className="cart-count-pill">{cartItems.length}</span>
            )}
          </h2>
          <button className="cart-drawer-close" onClick={() => setCartOpen(false)}>✕</button>
        </div>

        {cartItems.length === 0 ? (
          <div className="cart-empty">
            <span className="cart-empty-icon">🛵</span>
            <p>Your cart is empty.</p>
            <p className="cart-empty-sub">Add a bike from the booking page.</p>
          </div>
        ) : (
          <>
            <div className="cart-items-list">
              {cartItems.map((item) => (
                <div key={item.id} className="cart-item-card">
                  <img
                    src={getBikeImageUrl(item.bikeImage)}
                    alt={item.bikeName}
                    className="cart-item-img"
                  />

                  <div className="cart-item-info">
                    <div className="cart-item-name">{item.bikeName}</div>
                    <div className="cart-item-type">{item.bikeType}</div>

                    <div className="cart-item-dates">
                      <span>
                        📅{" "}
                        {new Date(item.startDate).toLocaleDateString("en-IN", {
                          day: "2-digit", month: "short",
                        })}
                      </span>
                      <span className="cart-date-arrow">→</span>
                      <span>
                        {new Date(item.endDate).toLocaleDateString("en-IN", {
                          day: "2-digit", month: "short", year: "numeric",
                        })}
                      </span>
                    </div>

                    <div className="cart-item-price">
                      {item.effectivePricePerDay !== item.pricePerDay ? (
                        <>
                          <span className="cart-ppd-surge">₹{item.effectivePricePerDay}/day</span>
                          <span className="cart-ppd-original">₹{Math.round(item.effectivePricePerDay * 1.4)}/day</span>
                        </>
                      ) : (
                        <>
                          <span className="cart-ppd">₹{item.pricePerDay}/day</span>
                          <span className="cart-ppd-original" style={{ marginLeft: "6px" }}>₹{Math.round(item.pricePerDay * 1.4)}/day</span>
                        </>
                      )}
                    </div>

                    <div className="cart-item-total">
                      Total: <strong>₹{item.totalAmount}</strong>
                    </div>

                    {/* ── Proceed to Payment per item ── */}
                    <button
                      className="cart-btn-proceed"
                      onClick={() => handleProceed(item)}
                      disabled={loadingId === item.id}
                    >
                      {loadingId === item.id ? "Processing…" : "Proceed to Payment →"}
                    </button>
                  </div>

                  {/* Remove button — uses its own isRemoving state, independent of proceed */}
                  <button
                    className="cart-btn-remove"
                    onClick={() => removeFromCart(item.id)}
                    title="Remove from cart"
                    disabled={isRemoving(item.id)}
                  >
                    {isRemoving(item.id) ? "…" : "🗑️"}
                  </button>
                </div>
              ))}
            </div>

            {/* ── Footer: grand total + Proceed to Payment ── */}
            <div className="cart-footer">
              <div className="cart-grand-total">
                <span>Grand Total ({cartItems.length} {cartItems.length === 1 ? "bike" : "bikes"})</span>
                <strong>₹{totalCartValue}</strong>
              </div>

              <button
                className="cart-btn-proceed-all"
                onClick={handleProceedAll}
                disabled={proceedingAll || cartItems.length === 0}
              >
                {proceedingAll ? (
                  "Processing Bookings…"
                ) : (
                  <>Proceed to Payment (₹{totalCartValue}) →</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default CartDrawer;
